import { Component, inject, ViewChild, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay, filter } from 'rxjs/operators';

// Imports
import { FoldersComponent } from '../app/features/folders/folders.component';
import { MessageDetailComponent } from '../app/features/message-detail/message-detail.component';
import { MessageListComponent } from '../app/features/message-list/message-list.component';
import { DraftListComponent } from '../app/features/draft-list/draft-list.component';
import { AuthService } from './core/auth/auth.service';
import { ComposeDialogComponent } from './features/compose-dialog/compose-dialog.component';
import { EdeskSelectorComponent } from './features/edesk-selector/edesk-selector.component';
import { SendGeneralAgendaRequest } from './api-draft';
import { EdeskService, RecipientDto, LoadDraftResponse } from './core/edesk.service';
import { EdeskStateService } from './core/edesk-state.service';
import { Delegation } from './api/model/delegation';

import { SignNotificationService } from './core/sign-notification.service';

// Make sure to add other imports back in: FoldersComponent, MessageDetailComponent

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MessageListComponent, 
    FoldersComponent,
    MessageDetailComponent,
    ComposeDialogComponent,
    DraftListComponent,
    EdeskSelectorComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['../styles.scss']
})
export class AppComponent implements OnInit {
title = 'qsign-angular-edesk-demo';
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  public authService = inject(AuthService);
  private edeskService = inject(EdeskService);
  public edeskStateService = inject(EdeskStateService);
  // 2. INJECT SERVICE
  private signNotificationService = inject(SignNotificationService);

  @ViewChild(ComposeDialogComponent) composeDialog!: ComposeDialogComponent;
  @ViewChild('draftListComponent') draftListComponent?: DraftListComponent;
  @ViewChild(FoldersComponent) foldersComp?: FoldersComponent;
  @ViewChild(MessageListComponent) messageListComp!: MessageListComponent;

  // eDesk selection state
  delegations = signal<Delegation[]>([]);
  showEdeskSelector = signal(false);
  delegationsLoaded = signal(false);

  async ngOnInit() {
    console.log('AppComponent ngOnInit');
    console.log('Current URL:', this.router.url);

    // After each completed Angular navigation, attempt silent login (prompt=none)
    // if the user is not authenticated and is NOT already on a login/callback route.
    // Using NavigationEnd guarantees event.url is the fully-resolved destination URL,
    // which avoids the race condition where window.location.pathname still reflects
    // the previous page during an in-app (SPA) link click.
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      console.log('AppComponent: NavigationEnd:', event.url);
      if (!this.authService.isLoggedIn() &&
          !event.url.startsWith('/login') &&
          !event.url.startsWith('/auth/callback') &&
          !event.url.startsWith('/sso-redirect') &&
          !window.location.search.includes('error=')) {
        console.log('AppComponent: Attempting silent login for route:', event.url);
        this.authService.trySilentLogin();
      }
    });

    // Subscribe to auth status changes for redirect logic
    this.authService.isAuthenticated$.subscribe(isAuth => {
      console.log('AppComponent: Auth status changed:', isAuth);
      const currentPath = window.location.pathname;

      if (!isAuth && !currentPath.includes('/login') && !currentPath.includes('/auth/callback') && !currentPath.includes('/sso-redirect')) {
        console.log('Presmerovavam na /login');
        this.router.navigate(['/login']);
      } else if (isAuth) {
        console.log('User authenticated, loading recipients and delegations...');
        this.loadRecipients();
        this.loadDelegations();
      }
    });
  }

  isDesktop$: Observable<boolean> = this.breakpointObserver
    .observe('(min-width: 75em)') 
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  mobileMenuOpen = false;
  filterOverlayOpen = false;
  
  // Data State - nezacina s vybranym priecinkom
  selectedFolderId: number | null = null;
  selectedFolderName: string = 'Elektronické správy';
  selectedMessageId: number | null = null;
  selectedMessageClass: string | null = null;
  isDraftsFolder = false; // Flag to indicate if drafts folder is selected

  // Filter State
  filterQuery = '';
  pageSize = 10;

  // --- Filter Actions ---

  updateFilter(ev: Event) {
    this.filterQuery = (ev.target as HTMLInputElement).value;
  }

  updatePageSize(ev: Event) {
    this.pageSize = Number((ev.target as HTMLSelectElement).value);
  }

  clearFilters() {
    this.filterQuery = '';
    this.pageSize = 10;
    this.closeFilterOverlay();
  }

  refreshList() {
    if (this.messageListComp) {
      this.messageListComp.refresh();
    }
  }

  // --- Navigation Actions ---

  toggleMobileMenu() { this.mobileMenuOpen = !this.mobileMenuOpen; }
  closeMobileMenu() { this.mobileMenuOpen = false; }

  toggleFilterOverlay() { this.filterOverlayOpen = !this.filterOverlayOpen; }
  closeFilterOverlay() { this.filterOverlayOpen = false; }

  onFolderSelected(data: {id: number, name: string}) {
    this.selectedFolderId = data.id;
    this.selectedFolderName = data.name;
    this.selectedMessageId = null;
    this.isDraftsFolder = data.id === -1; // Check if it's the special drafts folder
    this.closeMobileMenu(); 
  }

  onMessageSelected(data: {id: string, class: string}) {
    this.selectedMessageId = Number(data.id);
    this.selectedMessageClass = data.class;
  }

  clearMessageSelection() {
    this.selectedMessageId = null;
    this.selectedMessageClass = null;
  }

  // --- Auth Actions ---
  
  logout() {
    this.authService.logout();
  }

  getUserName(): string {
    return this.authService.getUserName();
  }

  // --- Compose Dialog State ---
  
  composeOpen = false;
  recipients = signal<RecipientDto[]>([]);

  onComposeRequested() {
    // Ensure recipients are loaded before opening dialog
    if (this.recipients().length === 0) {
      console.log('Recipients not loaded yet, loading now...');
      this.loadRecipients();
    }
    
    this.composeOpen = true;
    // Reset form when opening for new message
    setTimeout(() => {
      if (this.composeDialog) {
        this.composeDialog.resetForm();
      }
    }, 0);
  }

  onDraftSelected(draftId: number) {
    // Ensure recipients are loaded before opening dialog
    if (this.recipients().length === 0) {
      console.log('Recipients not loaded yet, loading now...');
      this.loadRecipients();
    }
    
    // Open the dialog first
    this.composeOpen = true;
    
    // Use setTimeout to ensure the dialog is rendered
    setTimeout(() => {
      if (this.composeDialog) {
        this.composeDialog.loadDraft(draftId).catch(err => {
          console.error('Failed to load draft:', err);
          // Show error - you can enhance this with a toast notification
          alert('Nepodarilo sa načítať rozpracovanú správu.');
          this.composeOpen = false;
        });
      }
    }, 0);
  }

  closeCompose() {
    this.composeOpen = false;
    // Refresh draft list if we're viewing the drafts folder
    if (this.isDraftsFolder && this.draftListComponent) {
      this.draftListComponent.refresh();
    }
  }

  onMessageSent(_req: SendGeneralAgendaRequest) {
    // Refresh draft list after sending (draft is deleted from backend)
    if (this.isDraftsFolder && this.draftListComponent) {
      this.draftListComponent.refresh();
    }
    console.log('Message sent:', _req);
  }

  loadRecipients() {
    this.edeskService.getRecipients(undefined, 1000).subscribe({
      next: (data) => {
        console.log('Loaded recipients:', data.length);
        this.recipients.set(data);
      },
      error: (err) => {
        console.error('Failed to load recipients:', err);
        // Keep empty array on error
      }
    });
  }

  loadDelegations() {
    this.edeskService.getDelegations().subscribe({
      next: (response) => {
        const list = response.delegations ?? [];
        console.log('Loaded delegations:', list.length);
        this.delegations.set(list);
        this.edeskStateService.setDelegations(list);

        const existingUri = this.edeskStateService.selectedEdeskUri();
        const isValidExisting = existingUri != null && list.some(d => d.edesk?.uri === existingUri);

        if (isValidExisting) {
          // Already have a valid selection restored from sessionStorage — skip selector
          console.log('Restored eDesk URI from session:', existingUri);
        } else if (list.length === 1) {
          // Auto-select single eDesk
          const uri = list[0].edesk?.uri;
          if (uri) {
            this.edeskStateService.setSelectedEdeskUri(uri);
            console.log('Auto-selected eDesk URI:', uri);
          }
        } else if (list.length > 1) {
          // Show selector modal — user must choose
          this.showEdeskSelector.set(true);
        }
        this.delegationsLoaded.set(true);
      },
      error: (err) => {
        console.error('Failed to load delegations:', err);
        this.delegationsLoaded.set(true);
      }
    });
  }

  onEdeskSelected(_uri: string) {
    this.showEdeskSelector.set(false);
    // Reset folder/message selection — they belong to the previous eDesk
    this.selectedFolderId = null;
    this.selectedMessageId = null;
    this.selectedMessageClass = null;
    this.selectedFolderName = 'Elektronické správy';
    this.isDraftsFolder = false;
    // Reload folders for the newly selected eDesk
    this.foldersComp?.reload();
  }

  openEdeskSelector() {
    this.showEdeskSelector.set(true);
  }

  get foldersError(): string | null {
    return this.foldersComp?.error() ?? null;
  }

  get foldersLoading(): boolean {
    return this.foldersComp?.loading() ?? false;
  }

  get messagesLoading(): boolean {
    return this.messageListComp?.loading() ?? false;
  }
}